// @flow

import { jitsiLocalStorage } from '@jitsi/js-utils';
import { sha256 } from 'js-sha256';
import _ from 'lodash';
import moment from 'moment';

import { apiKEY, mtDomain } from '../config/constants';
import { fetchAxiosApi } from '../mtApi/functions';

import type { ConferenceInfo } from './roomInfo';

declare var config: Object;

/**
 * Last-resort read of the participant count, from the Meet Hour backend.
 *
 * Only reached when both the XMPP push and Prosody's HTTP endpoint are
 * unavailable - typically a firewall blocking websocket and BOSH on a deployment
 * that has not enabled ENABLE_ROOM_INFO_API. Without it, a waiting guest on such
 * a deployment would never be released at all.
 *
 * The backend serves a replicated copy of the room that trails it, so this is the
 * slowest and least accurate of the three sources, which is why it sits last.
 *
 * Lives here rather than in the prejoin screens so those screens keep no API
 * plumbing of their own.
 *
 * @param {Object} options - Request options.
 * @param {string} options.roomName - Backend-safe room name.
 * @param {string} options.accessToken - Access token from redux, if any.
 * @param {Object} options.apiConfig - API_URL_PREFIX/API_VERSION_PREFIX, needed
 * on native where there is no global config.
 * @param {string} options.clientId - MH client id; falls back to global config.
 * @returns {Promise<?ConferenceInfo>} Null when the backend could not answer.
 */
export async function fetchBackendConferenceInfo({
    roomName,
    accessToken,
    apiConfig,
    clientId
}: Object = {}): Promise<?ConferenceInfo> {
    if (!roomName) {
        return null;
    }

    const moement = moment().seconds(0)
        .milliseconds(0)
        .format('X');
    const mesh256 = sha256(`${apiKEY}:MH:${moement}`);
    const userAccessToken = jitsiLocalStorage.getItem('accessToken') ?? accessToken;

    const headers = _.isNil(userAccessToken)
        ? { 'Content-Type': 'application/json' }
        : {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userAccessToken}`
        };

    const response = await fetchAxiosApi('meeting/totalparticipants', {
        method: 'post',
        headers,
        body: JSON.stringify({
            client_id: clientId ?? (typeof config === 'undefined' ? undefined : config?.MH_CLIENT_ID),
            credentials: mesh256,
            domain: mtDomain,

            meeting_id: roomName
        }),
        config: apiConfig
    });

    const total = response?.data?.total_participant;

    if (_.isNil(total)) {
        return null;
    }

    const participants = Number(total);

    // The backend reports a count and nothing else, so host presence has to be
    // inferred from it - the same assumption the prejoin screens already made
    // before any of this existed.
    return {
        exists: participants > 0,
        participants,
        moderators: 0,
        hasHost: false,
        lobbyEnabled: false,
        membersOnly: false
    };
}
